import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, 
  Upload, 
  X, 
  Sparkles,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  Eye,
  Download,
  Clock,
  AlertCircle,
  Edit,
  Trash2
} from "lucide-react";

const IMAGE_TYPES = [
  { id: "front", label: "Front View", icon: "📷" },
  { id: "back", label: "Back View", icon: "🔄" },
  { id: "side", label: "Side View", icon: "↔️" },
  { id: "zoom", label: "Detail/Zoom", icon: "🔍" }
];

const ProductDetail = () => {
  const navigate = useNavigate();
  const { skuId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchProduct();
  }, [skuId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/skus/${skuId}`, { withCredentials: true });
      setProduct(response.data);
    } catch (error) {
      toast.error("Failed to load product");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      // Upload file first
      const formData = new FormData();
      formData.append("file", file);
      
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Update SKU images
      await axios.post(`${API}/skus/${skuId}/images`, {
        images: { [type]: uploadResponse.data.url }
      }, { withCredentials: true });
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} image updated`);
      fetchProduct();
    } catch (error) {
      toast.error(`Failed to upload ${type} image`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteImage = async (type) => {
    if (!confirm(`Delete ${type} image?`)) return;
    
    try {
      await axios.delete(`${API}/skus/${skuId}/images/${type}`, { withCredentials: true });
      toast.success("Image deleted");
      fetchProduct();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { color: "bg-gray-100 text-gray-700 border-gray-300", label: "Draft" },
      ready: { color: "bg-blue-100 text-blue-700 border-blue-300", label: "Ready for Shoot" },
      processing: { color: "bg-yellow-100 text-yellow-700 border-yellow-300", label: "Processing" },
      completed: { color: "bg-green-100 text-green-700 border-green-300", label: "Completed" },
      failed: { color: "bg-red-100 text-red-700 border-red-300", label: "Failed" }
    };
    return configs[status] || configs.draft;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!product) return null;

  const imageStatus = product.image_status || {};
  const images = product.images || {};
  const canCreateShoot = imageStatus.front;
  const uploadedCount = Object.values(imageStatus).filter(v => v).length;
  const statusConfig = getStatusConfig(product.status);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/products")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{product.sku_name}</h1>
              <p className="text-gray-500">SKU: {product.sku_code || product.sku_id.slice(-8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            <Button
              onClick={() => navigate(`/products/${skuId}/shoot`)}
              disabled={!canCreateShoot}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-shoot-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create AI Shoot
            </Button>
          </div>
        </div>

        {/* Product Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Product Images */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Product Images</CardTitle>
                  <Badge variant={uploadedCount > 0 ? "default" : "outline"}>
                    {uploadedCount}/4 Uploaded
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {IMAGE_TYPES.map(({ id, label, icon }) => (
                    <div 
                      key={id}
                      className={`
                        border-2 rounded-lg p-3 
                        ${images[id] ? 'border-green-200 bg-green-50' : 'border-dashed border-gray-300'}
                      `}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span>{icon}</span>
                          <span className="font-medium text-sm">{label}</span>
                          {id === "front" && <Badge variant="outline" className="text-xs">Required</Badge>}
                        </div>
                        {images[id] && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                      </div>
                      
                      {images[id] ? (
                        <div className="relative group">
                          <img
                            src={images[id].image_url}
                            alt={label}
                            className="w-full h-40 object-contain rounded bg-white"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-2">
                            <label className="p-2 bg-white rounded-full cursor-pointer hover:bg-gray-100">
                              <Edit className="w-4 h-4 text-gray-700" />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(id, e.target.files[0])}
                              />
                            </label>
                            <button 
                              onClick={() => handleDeleteImage(id)}
                              className="p-2 bg-white rounded-full hover:bg-gray-100"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                          {uploading[id] && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
                              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-40 cursor-pointer bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                          {uploading[id] ? (
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                          ) : (
                            <>
                              <Upload className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-500">Click to upload</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(id, e.target.files[0])}
                            disabled={uploading[id]}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
                
                {!canCreateShoot && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">
                      Upload a front view image to enable AI photoshoot creation.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Product Details & Shoots */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Product Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium capitalize">{product.category}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Created</span>
                  <span>{new Date(product.created_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Recent Shoots */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">AI Shoots</CardTitle>
              </CardHeader>
              <CardContent>
                {product.shoots && product.shoots.length > 0 ? (
                  <div className="space-y-3">
                    {product.shoots.slice(0, 5).map(shoot => (
                      <div 
                        key={shoot.shoot_id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                        onClick={() => navigate(`/shoots/${shoot.shoot_id}`)}
                      >
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">{shoot.assets?.length || 0} assets</span>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">
                          {shoot.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No shoots yet</p>
                    {canCreateShoot && (
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate(`/products/${skuId}/shoot`)}
                      >
                        Create First Shoot
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProductDetail;
